import React, { useEffect, useMemo, useState } from "react";
import { UserPlus, Users, X, Crown } from "lucide-react";
import { toast } from "sonner";

import { v2Api } from "@/lib/v2Api";
import { confirm } from "@/lib/confirm";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function avatarFromName(name) {
  const parts = (name || "?").trim().split(/\s+/);
  return ((parts[0]?.[0] || "?") + (parts[1]?.[0] || "")).toUpperCase();
}

function colorFor(seed) {
  const palette = [
    "bg-blue-100 text-blue-800",
    "bg-emerald-100 text-emerald-800",
    "bg-amber-100 text-amber-800",
    "bg-rose-100 text-rose-800",
    "bg-violet-100 text-violet-800",
    "bg-teal-100 text-teal-800",
    "bg-cyan-100 text-cyan-800",
    "bg-pink-100 text-pink-800",
  ];
  let h = 0;
  for (const c of String(seed)) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return palette[h % palette.length];
}

function AddMemberDialog({ open, onOpenChange, projectId, users, onAdded }) {
  const [userId, setUserId] = useState("");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setUserId(""); setSearch(""); }
  }, [open]);

  // Exclude admins (they're always in every tender channel automatically)
  const eligible = useMemo(() =>
    users.filter((u) => u.workspace_role_code !== "admin"),
    [users]
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return q
      ? eligible.filter((u) =>
          u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
        )
      : eligible;
  }, [eligible, search]);

  const selected = eligible.find((u) => u.id === userId);

  const submit = async () => {
    if (!userId) { toast.error("Pick a user"); return; }
    setBusy(true);
    try {
      await v2Api.projects.addMember(projectId, { user_id: userId });
      toast.success("Member added");
      onOpenChange(false);
      onAdded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Could not add member");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add member to project</DialogTitle>
          <DialogDescription>
            Admins have access by default. Pick a manager or member below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Input
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setUserId(""); }}
            autoFocus
          />
          <div className="border rounded-md overflow-y-auto max-h-56">
            {filtered.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">No users found</p>
            ) : (
              filtered.map((u) => {
                const active = u.id === userId;
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => setUserId(u.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left transition hover:bg-slate-50 ${
                      active ? "bg-blue-50" : ""
                    }`}
                  >
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${colorFor(u.email)}`}>
                      {avatarFromName(u.name)}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium truncate">{u.name}</span>
                      <span className="block text-xs text-slate-400 truncate">{u.email}</span>
                    </span>
                    <Badge variant="outline" className={`text-[11px] shrink-0 ${
                      u.workspace_role_code === "manager"
                        ? "border-blue-300 text-blue-700 bg-blue-50"
                        : ""
                    }`}>
                      {u.workspace_role_name || u.legacy_role || "member"}
                    </Badge>
                    {active && <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
          {selected && (
            <p className="text-xs text-slate-500">
              Selected: <span className="font-medium text-slate-700">{selected.name}</span>
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !userId}>
            {busy ? "Adding…" : "Add member"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MembersPanel({ project, canManage = false, onMembersChange }) {
  const [members, setMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [filter, setFilter] = useState("");

  const refresh = async () => {
    if (!project?.id) return;
    const [mRes, uRes] = await Promise.allSettled([
      v2Api.projects.members(project.id),
      v2Api.workspaceMembers(),
    ]);
    if (mRes.status === "fulfilled") {
      setMembers(mRes.value || []);
      onMembersChange?.(mRes.value || []);
    }
    if (uRes.status === "fulfilled") setUsers(uRes.value || []);
    const failed = [mRes, uRes]
      .map((r, i) => r.status !== "fulfilled" && ["members", "users"][i])
      .filter(Boolean);
    if (failed.length) toast.error(`Could not load: ${failed.join(", ")}`);
  };

  useEffect(() => { refresh(); }, [project?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (m) => {
    if (!await confirm(`Remove ${m.user_name} from this project?`, { title: "Remove member", confirmLabel: "Remove" })) return;
    try {
      await v2Api.projects.removeMember(project.id, m.user_id);
      toast.success("Removed");
      refresh();
    } catch {
      toast.error("Could not remove");
    }
  };

  // Admins always have access — don't list them as explicit members
  const nonAdminMembers = useMemo(
    () => members.filter((m) => m.role_code !== "admin"),
    [members],
  );

  const filtered = useMemo(() => {
    if (!filter.trim()) return nonAdminMembers;
    const q = filter.toLowerCase();
    return nonAdminMembers.filter(
      (m) =>
        m.user_name.toLowerCase().includes(q) ||
        m.user_email.toLowerCase().includes(q) ||
        m.role_name.toLowerCase().includes(q),
    );
  }, [nonAdminMembers, filter]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-600" /> Project members
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Managers and members with direct access. Admins always have access and are not listed here.
            </p>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setOpenAdd(true)}>
              <UserPlus className="w-3.5 h-3.5 mr-1" /> Add member
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <Input
          placeholder="Filter by name, email or role"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-sm h-9"
        />
        {filtered.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-6">
            {nonAdminMembers.length === 0
              ? canManage ? "No members yet. Click Add member to get started." : "No members added directly to this project."
              : "No members match the filter."}
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {filtered.map((m) => (
              <li key={m.id + (m.via_team_id || "")} className="flex items-center gap-3 py-2.5">
                <span className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold ${colorFor(m.user_email)}`}>
                  {avatarFromName(m.user_name)}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="font-medium text-sm truncate">{m.user_name}</p>
                    {m.role_code === "admin" && <Crown className="w-3.5 h-3.5 text-amber-500" />}
                  </div>
                  <p className="text-xs text-slate-500 truncate">{m.user_email}</p>
                </div>
                <Badge
                  variant="outline"
                  className={`text-[11px] shrink-0 ${
                    m.role_code === "manager"
                      ? "border-blue-300 text-blue-700 bg-blue-50"
                      : m.role_code === "admin"
                      ? "border-amber-300 text-amber-700 bg-amber-50"
                      : ""
                  }`}>
                  {m.role_name}
                </Badge>
                {canManage && (
                  <button type="button"
                    className="text-slate-400 hover:text-rose-500 ml-1"
                    onClick={() => remove(m)}
                    title="Remove member">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      <AddMemberDialog
        open={openAdd}
        onOpenChange={setOpenAdd}
        projectId={project?.id}
        users={users}
        onAdded={refresh}
      />
    </Card>
  );
}
